import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { waLinkBusiness } from "@/lib/wa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit, to10Digit } from "@/lib/phone";
import { toast } from "sonner";
import {
    Sparkle, ArrowRight, ArrowLeft, CheckCircle, MagnifyingGlass,
    Clock, Lightbulb, CalendarBlank, WhatsappLogo, Camera,
    Phone, ClipboardText, Tooth
} from "@phosphor-icons/react";

const QUESTIONS = [
    {
        key: "concern",
        question: "What is your main concern with your smile?",
        options: [
            { id: "crowding", label: "Crowding (teeth too close together)" },
            { id: "spacing", label: "Spacing or gaps" },
            { id: "overbite", label: "Overbite or underbite" },
            { id: "rotated", label: "Crooked or rotated teeth" },
            { id: "general", label: "General alignment" },
        ],
    },
    {
        key: "severity",
        question: "How would you describe the severity?",
        options: [
            { id: "mild", label: "Mild, small adjustments needed" },
            { id: "moderate", label: "Moderate, visible but not severe" },
            { id: "severe", label: "Severe, significant correction needed" },
            { id: "unsure", label: "I'm not sure" },
        ],
    },
    {
        key: "age",
        question: "What's your age range?",
        options: [
            { id: "12-17", label: "12 to 17 (Teen)" },
            { id: "18-25", label: "18 to 25" },
            { id: "26-35", label: "26 to 35" },
            { id: "36-45", label: "36 to 45" },
            { id: "46+", label: "46+" },
        ],
    },
    {
        key: "history_braces",
        question: "Have you had braces or aligners before?",
        options: [
            { id: "yes", label: "Yes, I've had braces or aligners" },
            { id: "no", label: "No, never" },
            { id: "unsure", label: "I'm not sure / I don't remember" },
        ],
    },
    {
        key: "timeline",
        question: "When would you like to start orthodontic treatment?",
        options: [
            { id: "start_0_1_month", label: "In the next 0 to 1 month" },
            { id: "start_1_3_months", label: "In the next 1 to 3 months" },
            { id: "start_3_6_months", label: "In the next 3 to 6 months" },
            { id: "start_not_sure", label: "Just exploring / not sure yet" },
        ],
    },
];

const CONCERN_LABELS = {
    crowding: "Crowding",
    spacing: "Spacing",
    overbite: "Overbite/Underbite",
    rotated: "Rotated teeth",
    general: "General alignment",
};

// Helper text shown after a user selects an answer for certain questions.
// Keys correspond to option ids in the QUESTIONS array above.
const HELPER_TEXTS = {
    concern: {
        crowding:
            "Crowding often needs careful alignment planning to protect both smile health and bite comfort, your specialist will walk you through the options.",
        spacing:
            "Spacing and gaps are usually manageable, and planning focuses on closing them naturally over a series of aligners.",
        overbite:
            "Bite concerns like overbite or underbite need proper assessment so your treatment expectations stay accurate from day one.",
        rotated:
            "Rotation and crooked teeth typically respond well to targeted corrections, planned around your case scan.",
        general:
            "Got it, this helps your specialist zero in on your primary goal for a more even, confident smile.",
    },
    severity: {
        mild:
            "Small, targeted adjustments are often an ideal match for clear aligners, your treatment may be shorter than you'd expect.",
        moderate:
            "Moderate cases are well within the range of clear aligner therapy, based on a proper assessment.",
        severe:
            "More detailed orthodontic planning may be needed depending on your bite and tooth movement, your specialist will explain what's involved.",
        unsure:
            "No worries, your specialist will help estimate severity after a quick visual evaluation.",
    },
    age: {
        "12-17":
            "Heads up, if you're under 18, your specialist may ask for parent or guardian involvement before the next steps.",
        "18-25":
            "Noted, your specialist will tailor next steps based on your case and preferences.",
        "26-35":
            "Noted, your specialist will tailor next steps based on your case and preferences.",
        "36-45":
            "Noted, your specialist will tailor next steps based on your case and preferences.",
        "46+":
            "Noted, your specialist will tailor next steps based on your case and preferences.",
    },
    history_braces: {
        yes:
            "Previous treatment is useful context, it helps your specialist understand what has (or hasn't) worked for you before.",
        no:
            "Since you haven't had braces before, your specialist will walk you through the process and what to expect.",
        unsure:
            "No problem, your specialist will confirm your treatment history during the assessment.",
    },
    timeline: {
        start_0_1_month:
            "Knowing your timeline helps your specialist plan the next steps quickly and book you in promptly.",
        start_1_3_months:
            "Plenty of runway, your specialist will share a suitable plan based on your case and timeline.",
        start_3_6_months:
            "Smart approach, your specialist will help map out the best start schedule for you.",
        start_not_sure:
            "Totally fine, your specialist will discuss options and a realistic timeline once your case is evaluated.",
    },
};

function estimate(answers) {
    // Base cost matrix (INR)
    const base = { mild: 150000, moderate: 200000, severe: 280000, unsure: 200000 };
    const monthsMap = { mild: 6, moderate: 9, severe: 14, unsure: 9 };
    const concernAdj = { crowding: 1.05, spacing: 1.0, overbite: 1.15, rotated: 1.1, general: 1.0 };

    let cost = base[answers.severity] || 200000;
    cost = Math.round(cost * (concernAdj[answers.concern] || 1.0));

    let months = monthsMap[answers.severity] || 9;
    if (answers.concern === "overbite") months += 2;
    if (answers.timeline === "start_0_1_month" && answers.severity === "mild") months = Math.max(4, months - 1);

    return {
        costMin: Math.round(cost * 0.92),
        costMax: Math.round(cost * 1.08),
        months,
    };
}

const fmt = (n) => "₹" + n.toLocaleString("en-IN");

function generateSlots() {
    const now = new Date();
    const result = [];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const formatTime = (date) => {
        const h = date.getHours();
        const hr12 = ((h + 11) % 12) + 1;
        const ampm = h >= 12 ? "PM" : "AM";
        return `${hr12}:00 ${ampm}`;
    };
    const formatLabel = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(date);
        target.setHours(0, 0, 0, 0);
        const diff = (target - today) / (1000 * 60 * 60 * 24);
        if (diff === 0) return `Today, ${formatTime(date)}`;
        if (diff === 1) return `Tomorrow, ${formatTime(date)}`;
        return `${dayNames[date.getDay()]}, ${formatTime(date)}`;
    };
    const push = (date) => result.push({
        iso: date.toISOString(),
        label: formatLabel(date),
        date,
    });
    const mk = (base, dayOffset, hr) => {
        const d = new Date(base);
        d.setDate(d.getDate() + dayOffset);
        d.setHours(hr, 0, 0, 0);
        return d;
    };

    // Today: next available slot, rounded up by 2 hours from now
    const next = new Date(now);
    next.setHours(next.getHours() + 2, 0, 0, 0);
    // Sunday (0) → skip today
    const isSunday = (d) => d.getDay() === 0;
    if (!isSunday(now) && next.getHours() >= 10 && next.getHours() <= 17) {
        push(next);
        const next2 = new Date(next);
        next2.setHours(next2.getHours() + 2);
        if (next2.getHours() <= 19) push(next2);
    }

    // Tomorrow slots: 11am, 4pm (skip if Sunday)
    let dayOffset = 1;
    while (result.length < 4 && dayOffset <= 4) {
        const tmp = mk(now, dayOffset, 11);
        if (!isSunday(tmp)) {
            if (result.length < 4) push(mk(now, dayOffset, 11));
            if (result.length < 4) push(mk(now, dayOffset, 16));
        }
        dayOffset += 1;
    }
    return result.slice(0, 4);
}


/**
 * SmileAssessment
 *
 * Works two ways:
 *   1) Standalone page — mounted at `/smile-assessment`. Reads
 *      `?doctor=<id>` and `?embed=1` from the URL.
 *   2) Inline embed — imported and rendered directly by another
 *      component (e.g. DoctorLandingPage's Assessment tab). Pass
 *      `attributedDoctorIdProp` and `isEmbedProp` as props; these
 *      override the URL. This avoids the ~500ms – 2s iframe boot
 *      the tab used to pay every time it was clicked (iter100.22).
 *
 * Props take precedence over URL params so the standalone route
 * remains unchanged.
 */
export default function SmileAssessment({ attributedDoctorIdProp, isEmbedProp } = {}) {
    const navigate = useNavigate();
    // iter100.8 — Doctor landing page hands off the visited doctor via
    // ?doctor=<id> so this Smile Assessment attributes the resulting lead
    // to that specific doctor (source='doctor-landing', assigned to them).
    const [searchParams] = useSearchParams();
    const attributedDoctorId = attributedDoctorIdProp ?? searchParams.get("doctor") ?? null;
    // iter100.13 — `?embed=1` (URL, iframe embed) means we render as a
    // standalone page whose site header/footer should be hidden globally.
    // `isEmbedProp` (inline embed as a React child, e.g. inside the Doctor
    // Landing "Smile Assessment" tab) means we just render compactly WITHOUT
    // touching the parent page's chrome — the parent still owns its own
    // header. iter100.50 (2026-02-28) — Fixes bug where clicking the Smile
    // Assessment tab wiped the main site nav on the Doctor Landing Page.
    const isEmbed = isEmbedProp === true || searchParams.get("embed") === "1";
    const isIframeEmbed = searchParams.get("embed") === "1";
    useEffect(() => {
        if (!isIframeEmbed) return;
        document.body.classList.add("embed-mode");
        return () => document.body.classList.remove("embed-mode");
    }, [isIframeEmbed]);
    const [step, setStep] = useState(0); // 0..QUESTIONS.length-1, then contact step, then result
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ name: "", phone: "", email: "", city: "" });
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [leadId, setLeadId] = useState(null);
    const [pickedSlot, setPickedSlot] = useState(null);
    const [slotSubmitting, setSlotSubmitting] = useState(false);
    const slots = useMemo(() => generateSlots(), [submitted]);

    const totalSteps = QUESTIONS.length + 1; // questions + contact
    const isContactStep = step === QUESTIONS.length;
    const progress = Math.round(((step + (isContactStep ? 1 : 0)) / totalSteps) * 100);

    const currentQ = !isContactStep ? QUESTIONS[step] : null;
    const currentAnswer = currentQ ? answers[currentQ.key] : null;

    const next = () => {
        if (isContactStep) return;
        if (step + 1 <= QUESTIONS.length) setStep(step + 1);
    };
    const back = () => {
        if (step > 0) setStep(step - 1);
    };

    const validateContact = () => {
        const errs = {};
        if (!contact.name || contact.name.trim().length < 2) {
            errs.name = "Please enter your name";
        }
        const tenDigit = to10Digit(contact.phone);
        if (!isValid10Digit(contact.phone) || !/^[6-9]\d{9}$/.test(tenDigit)) {
            errs.phone = "Please enter a valid 10-digit mobile number";
        }
        if (!contact.city || contact.city.trim().length < 2) {
            errs.city = "Please enter your city";
        }
        if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
            errs.email = "Please enter a valid email address";
        }
        if (!consent) {
            errs.consent = "Please agree to be contacted before continuing";
        }
        return errs;
    };

    const submit = async () => {
        const errs = validateContact();
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            const concerns = Object.values(answers);
            const resp = await api.post("/smile-assessment", {
                name: contact.name,
                phone: toE164(contact.phone),
                email: contact.email,
                city: contact.city,
                concerns,
                answers,
                attributed_doctor_id: attributedDoctorId,
            });
            if (resp?.data?.id) setLeadId(resp.data.id);
            const est = estimate(answers);
            setResult(est);
            setSubmitted(true);
            toast.success("You're in! Now pick a time for your free call.");
        } catch {
            toast.error("Submission failed. Please try again.");
        }
        setSubmitting(false);
    };

    const pickSlot = async (slot) => {
        if (pickedSlot || slotSubmitting) return;
        setSlotSubmitting(true);
        try {
            if (leadId) {
                await api.post("/smile-assessment/preferred-slot", {
                    lead_id: leadId,
                    slot_label: slot.label,
                    slot_iso: slot.iso,
                });
            }
            setPickedSlot(slot);
            toast.success(`Got it! Dr. Meera will call you ${slot.label}.`);
        } catch {
            toast.error("Couldn't lock in that slot. Please try another.");
        } finally {
            setSlotSubmitting(false);
        }
    };

    if (submitted && result) {
        const firstName = (contact.name || "").trim().split(" ")[0] || "there";
        const callTime = pickedSlot?.label || "within 15 minutes";
        return (
            <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 lg:py-16" data-testid="assessment-result-page">
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center mb-10"
                >
                    <div className="relative inline-flex w-20 h-20 rounded-3xl bg-gradient-to-br from-amber-100 via-amber-50 to-primary/10 items-center justify-center mb-5 shadow-lg">
                        <span className="text-4xl" role="img" aria-label="celebration">🎉</span>
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 ring-4 ring-background flex items-center justify-center">
                            <CheckCircle size={12} weight="fill" color="white" />
                        </span>
                    </div>
                    <div className="overline text-primary mb-3">You're in</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        You're in, {firstName}! 🎉
                    </h1>
                    <p className="text-muted-foreground mt-3 max-w-lg mx-auto text-base">
                        Your assessment is locked in. Now pick a time for your free 15-minute call with a certified Invisique™ Patient Engagement Specialist.
                    </p>
                </motion.div>

                {/* (A) Time-slot picker card */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 }}
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-[#003E50] to-[#001a23] p-6 sm:p-8 bento-shadow mb-6"
                    data-testid="slot-picker-card"
                >
                    {/* decorative orbs */}
                    <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-cyan-400/15 blur-3xl pointer-events-none" aria-hidden="true" />
                    <div className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" aria-hidden="true" />

                    <div className="relative">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-11 h-11 rounded-2xl bg-white/12 backdrop-blur-md border border-white/15 flex items-center justify-center">
                                <CalendarBlank size={20} weight="fill" color="#fde68a" />
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-amber-200 mb-1">
                                    Step 1 of next steps
                                </div>
                                <h2 className="font-display text-xl sm:text-2xl font-bold text-white leading-tight">
                                    {pickedSlot ? "Slot locked in ✓" : "Pick a time for your free call"}
                                </h2>
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            {pickedSlot ? (
                                <motion.div
                                    key="confirmed"
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="p-5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15"
                                    data-testid="slot-confirmed"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-green-500/20 border border-green-400/30 flex items-center justify-center shrink-0">
                                            <CheckCircle size={20} weight="fill" color="#86efac" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-white font-display font-semibold text-lg">{pickedSlot.label}</div>
                                            <p className="text-white/70 text-sm mt-1">
                                                Dr. Meera will call <span className="font-semibold text-white">+91 {to10Digit(contact.phone)}</span> at this time. You'll get a WhatsApp reminder 15 minutes before.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="picker"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                        {slots.map((s, i) => (
                                            <button
                                                key={s.iso}
                                                onClick={() => pickSlot(s)}
                                                disabled={slotSubmitting}
                                                className="group relative p-3.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 hover:bg-white/15 hover:border-amber-300/50 hover:scale-[1.02] transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                                data-testid={`slot-option-${i}`}
                                            >
                                                <div className="flex items-center gap-1.5 text-amber-200 text-[10px] font-bold tracking-[0.15em] uppercase mb-1">
                                                    <Clock size={11} weight="fill" />
                                                    Free 15-min
                                                </div>
                                                <div className="text-white font-display font-bold text-sm sm:text-base leading-tight">
                                                    {s.label.split(",")[0]}
                                                </div>
                                                <div className="text-white/70 text-xs mt-0.5">
                                                    {s.label.split(",")[1]?.trim()}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-[11px] text-white/55 mt-4 flex items-center gap-1.5">
                                        <Phone size={11} weight="fill" />
                                        Slot times are in your local timezone · Mon, Sat, 10am, 7pm
                                    </p>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* WhatsApp side-CTA — hides when no business number is configured */}
                        {(() => {
                            const waUrl = waLinkBusiness(
                                process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER,
                                `Hi Invisique™ team, I just completed the Smile Assessment as ${firstName}. Can we chat?`
                            );
                            if (!waUrl) return null;
                            return (
                                <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
                                    <p className="text-white/70 text-sm">Need help right now?</p>
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold transition-colors shadow-md"
                                        data-testid="result-whatsapp-cta"
                                    >
                                        <WhatsappLogo size={16} weight="fill" />
                                        Chat on WhatsApp
                                    </a>
                                </div>
                            );
                        })()}
                    </div>
                </motion.div>

                {/* (C) Smile Simulator cross-sell */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-50 via-cyan-50/50 to-primary/5 border border-primary/15 p-6 sm:p-7 mb-6"
                    data-testid="simulator-cross-sell"
                >
                    <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-[#005a73] flex items-center justify-center shadow-lg shrink-0">
                            <Camera size={26} weight="fill" color="white" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="overline text-primary mb-1.5">While you wait</div>
                            <h3 className="font-display text-xl font-bold tracking-tight">
                                See your future smile in 10 seconds 📸
                            </h3>
                            <p className="text-muted-foreground text-sm mt-1.5 max-w-md">
                                Upload a selfie and our AI shows you a preview of your post-Invisique™ smile ,
                                free, instant, and powered by Nano Banana AI.
                            </p>
                        </div>
                        <Button
                            onClick={() => navigate("/smile-simulator")}
                            className="rounded-full h-11 px-5 shrink-0 w-full sm:w-auto"
                            data-testid="simulator-cross-sell-btn"
                        >
                            Try the simulator
                            <ArrowRight size={16} weight="bold" className="ml-2" />
                        </Button>
                    </div>
                </motion.div>

                {/* (E) What happens next, 3-step timeline */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 }}
                    className="bg-card border border-border rounded-3xl p-6 sm:p-7 bento-shadow mb-6"
                    data-testid="what-next-timeline"
                >
                    <div className="overline text-primary mb-5">What happens next</div>
                    <ol className="relative">
                        {[
                            {
                                icon: ClipboardText,
                                title: "Assessment done",
                                desc: "We have your case basics and contact details.",
                                state: "done",
                            },
                            {
                                icon: Phone,
                                title: `15-min specialist call · ${callTime}`,
                                desc: "Dr. Meera reviews your case, discusses options and estimated cost range, and answers your questions.",
                                state: pickedSlot ? "active" : "upcoming",
                            },
                            {
                                icon: Tooth,
                                title: "Free in-clinic scan & treatment plan",
                                desc: `If clear aligners are a fit, we'll match you with a certified Invisique™ provider in ${contact.city} for a free 3D scan.`,
                                state: "upcoming",
                            },
                        ].map((step, i, arr) => {
                            const Icon = step.icon;
                            const isLast = i === arr.length - 1;
                            const done = step.state === "done";
                            const active = step.state === "active";
                            return (
                                <li key={step.title} className="relative pl-14 pb-6 last:pb-0" data-testid={`timeline-step-${i + 1}`}>
                                    {!isLast && (
                                        <span
                                            className={`absolute left-[19px] top-10 bottom-0 w-px ${
                                                done ? "bg-primary/40" : "bg-border"
                                            }`}
                                            aria-hidden="true"
                                        />
                                    )}
                                    <span
                                        className={`absolute left-0 top-0 w-10 h-10 rounded-2xl flex items-center justify-center shadow-sm ${
                                            done
                                                ? "bg-primary text-white"
                                                : active
                                                ? "bg-amber-100 text-amber-700 ring-2 ring-amber-300"
                                                : "bg-secondary text-muted-foreground border border-border"
                                        }`}
                                    >
                                        {done ? (
                                            <CheckCircle size={20} weight="fill" />
                                        ) : (
                                            <Icon size={18} weight={active ? "fill" : "duotone"} />
                                        )}
                                    </span>
                                    <div className="font-display font-semibold text-foreground text-[15px] leading-snug">
                                        {step.title}
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {step.desc}
                                    </p>
                                </li>
                            );
                        })}
                    </ol>
                </motion.div>

                {/* Trust strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                    className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground"
                    data-testid="result-trust-strip"
                >
                    <span className="inline-flex items-center gap-1.5">
                        <Sparkle size={12} weight="fill" className="text-primary" />
                        <span className="font-bold text-foreground">237+ callbacks</span> scheduled this week
                    </span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1.5">
                        <CheckCircle size={12} weight="fill" className="text-primary" /> 100% confidential
                    </span>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1.5">
                        <CheckCircle size={12} weight="fill" className="text-primary" /> Free & no obligation
                    </span>
                </motion.div>
            </div>
        );
    }

    return (
        <div className={`max-w-3xl mx-auto px-5 sm:px-8 ${isEmbed ? "py-4" : "py-10 lg:py-16"}`} data-testid="smile-assessment-page">
            {/* iter100.21 — When rendered inside the doctor landing page's
                Assessment tab (embed=1), the tab already has its own header
                "Free Smile Assessment · 5 quick questions…", so we hide the
                big page-level hero to eliminate duplication + save vertical
                space (no scrolling required inside the tab). */}
            {!isEmbed && (
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-[0.2em] uppercase mb-5">
                        <Sparkle size={14} weight="fill" /> 60-second quiz
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
                        Smile Assessment
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
                        Answer a few quick questions to see if Invisique™ is right for you and get an
                        instant cost &amp; timeline estimate.
                    </p>
                </div>
            )}

            <div className={isEmbed ? "mb-4" : "mb-8"}>
                <div className="flex items-center justify-between text-xs mb-2">
                    <span className="overline text-muted-foreground">Progress</span>
                    <span className="font-semibold">{progress}% Complete</span>
                </div>
                <Progress value={progress} className="h-1.5" data-testid="assessment-progress" />
            </div>

            {/* Question card */}
            {currentQ && (
                <div className={`bg-card border border-border rounded-3xl bento-shadow ${isEmbed ? "p-5" : "p-6 sm:p-10"}`} data-testid={`assessment-step-${step + 1}`}>
                    <h2 className={`font-display font-bold ${isEmbed ? "text-lg sm:text-xl mb-4" : "text-2xl sm:text-3xl mb-8"}`}>
                        {currentQ.question}
                    </h2>
                    <div className={isEmbed ? "space-y-2" : "space-y-3"}>
                        {currentQ.options.map((o) => {
                            const active = currentAnswer === o.id;
                            return (
                                <button
                                    key={o.id}
                                    onClick={() => setAnswers({ ...answers, [currentQ.key]: o.id })}
                                    className={`w-full rounded-2xl border text-left flex items-center gap-3 transition-all ${
                                        isEmbed ? "p-3" : "p-4"
                                    } ${
                                        active
                                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                            : "border-border hover:border-primary/50 hover:bg-secondary/30"
                                    }`}
                                    data-testid={`option-${currentQ.key}-${o.id}`}
                                >
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                        active ? "border-primary bg-primary" : "border-border"
                                    }`}>
                                        {active && <div className="w-2 h-2 bg-white rounded-full" />}
                                    </div>
                                    <span className={`font-medium ${isEmbed ? "text-sm" : "text-base"}`}>{o.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Conditional helper text, shown after Q1 (concern) and Q2 (severity) */}
                    <AnimatePresence mode="wait">
                        {currentAnswer && HELPER_TEXTS[currentQ.key]?.[currentAnswer] && (
                            <motion.div
                                key={`${currentQ.key}-${currentAnswer}`}
                                initial={{ opacity: 0, y: -6, height: 0 }}
                                animate={{ opacity: 1, y: 0, height: "auto" }}
                                exit={{ opacity: 0, y: -6, height: 0 }}
                                transition={{ duration: 0.25, ease: "easeOut" }}
                                className="overflow-hidden"
                                data-testid={`helper-${currentQ.key}-${currentAnswer}`}
                            >
                                <div className={`flex items-start gap-3 rounded-2xl bg-gradient-to-r from-primary/5 via-amber-50/40 to-cyan-50/30 border border-primary/15 ${isEmbed ? "mt-3 p-3" : "mt-5 p-4"}`}>
                                    <div className={`rounded-xl bg-primary/10 flex items-center justify-center shrink-0 ${isEmbed ? "w-8 h-8" : "w-9 h-9"}`}>
                                        <Lightbulb size={isEmbed ? 16 : 18} weight="fill" className="text-primary" />
                                    </div>
                                    <p className={`leading-relaxed text-foreground/85 pt-0.5 ${isEmbed ? "text-xs" : "text-sm"}`}>
                                        {HELPER_TEXTS[currentQ.key][currentAnswer]}
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <div className={`flex justify-between ${isEmbed ? "mt-5" : "mt-10"}`}>
                        <Button variant="outline" onClick={back} disabled={step === 0} className={`rounded-full ${isEmbed ? "h-9 px-4 text-sm" : "h-11 px-5"}`} data-testid="assessment-back-btn">
                            <ArrowLeft size={16} className="mr-2" /> Back
                        </Button>
                        <Button onClick={next} disabled={!currentAnswer} className={`rounded-full ${isEmbed ? "h-9 px-4 text-sm" : "h-11 px-6"}`} data-testid="assessment-continue-btn">
                            Continue <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Contact step */}
            {isContactStep && (
                <div className={`bg-card border border-border rounded-3xl bento-shadow ${isEmbed ? "p-5" : "p-6 sm:p-10"}`} data-testid="assessment-contact-step">
                    <h2 className={`font-display font-bold ${isEmbed ? "text-lg sm:text-xl mb-1" : "text-2xl sm:text-3xl mb-2"}`}>
                        Get your treatment options
                    </h2>
                    <p className={`text-muted-foreground text-sm ${isEmbed ? "mb-4" : "mb-8"}`}>
                        Share your details and we'll connect you with a certified Invisique™ Patient Engagement Specialist for a free 15-minute call.
                    </p>
                    <div className={isEmbed ? "space-y-3" : "space-y-5"}>
                        {/* Full Name */}
                        <div>
                            <Label htmlFor="ca-name" className="text-sm font-semibold">Full Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="ca-name"
                                value={contact.name}
                                onChange={(e) => {
                                    setContact({ ...contact, name: e.target.value });
                                    if (errors.name) setErrors({ ...errors, name: undefined });
                                }}
                                placeholder="Enter your name"
                                aria-invalid={Boolean(errors.name)}
                                className={`h-12 mt-1.5 rounded-xl ${errors.name ? "border-red-500 focus-visible:ring-red-500/30" : ""}`}
                                data-testid="contact-name-input"
                            />
                            {errors.name && (
                                <p className="text-xs text-red-600 mt-1.5" data-testid="contact-name-error">{errors.name}</p>
                            )}
                        </div>

                        {/* Mobile + City row */}
                        <div className="grid sm:grid-cols-2 gap-4">
                            {/* Mobile Number */}
                            <div>
                                <Label htmlFor="ca-phone" className="text-sm font-semibold">Mobile Number <span className="text-red-500">*</span></Label>
                                <div className="mt-1.5">
                                    <PhoneInput10
                                        id="ca-phone"
                                        value={contact.phone}
                                        onChange={(v) => {
                                            setContact({ ...contact, phone: v });
                                            if (errors.phone) setErrors({ ...errors, phone: undefined });
                                        }}
                                        inputClassName={`h-12 ${errors.phone ? "border-red-500 focus-visible:ring-red-500/30" : ""}`}
                                        testid="contact-phone-input"
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="text-xs text-red-600 mt-1.5" data-testid="contact-phone-error">{errors.phone}</p>
                                )}
                            </div>

                            {/* City */}
                            <div>
                                <Label htmlFor="ca-city" className="text-sm font-semibold">City <span className="text-red-500">*</span></Label>
                                <div className="relative">
                                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none mt-[3px]" />
                                    <Input
                                        id="ca-city"
                                        value={contact.city}
                                        onChange={(e) => {
                                            setContact({ ...contact, city: e.target.value });
                                            if (errors.city) setErrors({ ...errors, city: undefined });
                                        }}
                                        placeholder="e.g., Delhi, Mumbai, Bengaluru"
                                        aria-invalid={Boolean(errors.city)}
                                        aria-describedby="ca-city-help"
                                        className={`h-12 mt-1.5 rounded-xl pl-9 ${errors.city ? "border-red-500 focus-visible:ring-red-500/30" : ""}`}
                                        data-testid="contact-city-input"
                                    />
                                </div>
                                {errors.city ? (
                                    <p className="text-xs text-red-600 mt-1.5" data-testid="contact-city-error">{errors.city}</p>
                                ) : null}
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <Label htmlFor="ca-email" className="text-sm font-semibold">Email Address</Label>
                            <Input
                                id="ca-email"
                                type="email"
                                value={contact.email}
                                onChange={(e) => {
                                    setContact({ ...contact, email: e.target.value });
                                    if (errors.email) setErrors({ ...errors, email: undefined });
                                }}
                                placeholder="you@example.com"
                                aria-invalid={Boolean(errors.email)}
                                aria-describedby="ca-email-help"
                                className={`h-12 mt-1.5 rounded-xl ${errors.email ? "border-red-500 focus-visible:ring-red-500/30" : ""}`}
                                data-testid="contact-email-input"
                            />
                            {errors.email ? (
                                <p className="text-xs text-red-600 mt-1.5" data-testid="contact-email-error">{errors.email}</p>
                            ) : null}
                        </div>

                        {/* Consent */}
                        <label htmlFor="ca-consent" className="flex items-start gap-3 p-3.5 rounded-2xl bg-secondary/40 border border-border cursor-pointer select-none" data-testid="contact-consent-label">
                            <Checkbox
                                id="ca-consent"
                                checked={consent}
                                onCheckedChange={(v) => {
                                    setConsent(Boolean(v));
                                    if (errors.consent) setErrors({ ...errors, consent: undefined });
                                }}
                                className="mt-0.5 shrink-0 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                data-testid="contact-consent-checkbox"
                            />
                            <div className="flex-1">
                                <span className="text-sm text-foreground font-medium leading-snug">
                                    I agree to be contacted about my treatment options.
                                </span>
                                <span className="block text-xs text-muted-foreground mt-1">
                                    Your details are used only for follow-up by our Patient Engagement team.
                                </span>
                                {errors.consent && (
                                    <span className="block text-xs text-red-600 mt-1" data-testid="contact-consent-error">{errors.consent}</span>
                                )}
                            </div>
                        </label>
                    </div>

                    <div className={`flex justify-between ${isEmbed ? "mt-5" : "mt-10"}`}>
                        <Button variant="outline" onClick={back} className={`rounded-full ${isEmbed ? "h-9 px-4 text-sm" : "h-11 px-5"}`} data-testid="contact-back-btn">
                            <ArrowLeft size={16} className="mr-2" /> Back
                        </Button>
                        <Button onClick={submit} disabled={submitting} className={`rounded-full ${isEmbed ? "h-9 px-4 text-sm" : "h-11 px-6"}`} data-testid="assessment-submit-btn">
                            {submitting ? "Submitting..." : "Request Free Call"}
                            <Sparkle size={16} weight="fill" className="ml-2" />
                        </Button>
                    </div>
                </div>
            )}

            {/* iter100.21 — Below-quiz simulator teaser is hidden when the
                quiz is embedded inside the doctor landing page's Assessment
                tab. That page already exposes a dedicated "Try Smile
                Preview" pill in the header, so this teaser would be a
                redundant CTA (and force scrolling inside the tab). Only
                shown on the standalone /smile-assessment page. */}
            {!isEmbed && (
                <Link to="/smile-simulator" className="block mt-6 group" data-testid="smile-simulator-teaser">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary/60 via-white to-amber-50/50 border border-primary/15 p-5 sm:p-6 hover:border-primary/40 hover:shadow-lg transition-all">
                        <div className="flex items-center gap-4">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-[#003E50] flex items-center justify-center shrink-0 shadow-md">
                                <Sparkle size={20} weight="fill" color="white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-semibold text-sm sm:text-base">
                                    Want to <span className="text-primary">see your new smile</span> first?
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                    Try our AI Smile Simulator, upload a selfie, get a 10-second preview.
                                </div>
                            </div>
                            <ArrowRight size={18} className="text-primary shrink-0 group-hover:translate-x-1 transition-transform" />
                        </div>
                    </div>
                </Link>
            )}
        </div>
    );
}
